<script setup lang="ts">
import { computed } from 'vue'
import { MapPinIcon } from '@heroicons/vue/24/outline'
import { highlight } from '@/utils/highlight'
import type { OsEligibilityResult } from '@/api/omnisearchApi'

const props = defineProps<{
  item: OsEligibilityResult
  query: string
}>()

const emit = defineEmits<{ navigate: [] }>()

function handleClick() {
  emit('navigate')
  window.location.href = `/eligibility/${props.item.uuid}`
}

/**
 * Retourne les champs qui contiennent réellement le terme recherché,
 * pour mettre en avant ce qui a déclenché l'apparition du résultat.
 */
const matchedIn = computed(() => {
  const q = props.query.trim().toLowerCase()
  if (!q) return []
  const fields: string[] = []
  if (props.item.fulladdress?.toLowerCase().includes(q)) fields.push('adresse')
  if (props.item.codeBatiment?.toLowerCase().includes(q)) fields.push('code bât.')
  if (props.item.city?.toLowerCase().includes(q)) fields.push('ville')
  if (props.item.zip?.toLowerCase().includes(q)) fields.push('CP')
  if (props.item.company?.name?.toLowerCase().includes(q)) fields.push('client')
  if (props.item.company?.siren?.toLowerCase().includes(q)) fields.push('SIREN')
  if (props.item.company?.siret?.toLowerCase().includes(q)) fields.push('SIRET')
  return fields
})
</script>

<template>
  <button
    class="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-base-200 transition-colors text-left group cursor-pointer"
    @click="handleClick"
  >
    <!-- Icône -->
    <span class="mt-0.5 flex-shrink-0 w-8 h-8 rounded-md bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
      <MapPinIcon class="w-4 h-4 text-teal-600 dark:text-teal-400" />
    </span>

    <div class="flex-1 min-w-0 space-y-0.5">

      <!-- Ligne 1 : adresse principale -->
      <p
        class="text-sm font-medium truncate text-base-content"
        v-html="highlight(item.fulladdress ?? item.codeBatiment ?? '—', query)"
      />

      <!-- Ligne 2 : ville + CP -->
      <p v-if="item.city || item.zip" class="text-xs text-base-content/60 truncate">
        <span v-if="item.zip" v-html="highlight(item.zip, query)" />
        <span v-if="item.zip && item.city"> </span>
        <span v-if="item.city" v-html="highlight(item.city, query)" />
      </p>

      <!-- Ligne 3 : code bâtiment (si pas déjà affiché en titre) -->
      <p
        v-if="item.codeBatiment && item.fulladdress"
        class="text-xs text-base-content/50"
      >
        Code bât. : <span v-html="highlight(item.codeBatiment, query)" />
      </p>

      <!-- Ligne 4 : client lié -->
      <div v-if="item.company" class="flex flex-wrap items-center gap-x-2 gap-y-0.5 pt-0.5">
        <span
          class="text-xs font-medium text-base-content/70"
          v-html="highlight(item.company.name ?? '—', query)"
        />
        <span
          v-if="item.company.siret"
          class="text-xs text-base-content/40"
        >
          SIRET <span v-html="highlight(item.company.siret, query)" />
        </span>
        <span
          v-else-if="item.company.siren"
          class="text-xs text-base-content/40"
        >
          SIREN <span v-html="highlight(item.company.siren, query)" />
        </span>
      </div>

      <!-- Ligne 5 : tags « match via … » -->
      <div v-if="matchedIn.length" class="flex flex-wrap gap-1 pt-0.5">
        <span
          v-for="field in matchedIn"
          :key="field"
          class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300"
        >
          {{ field }}
        </span>
      </div>

    </div>

    <span class="text-xs text-base-content/40 self-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">→</span>
  </button>
</template>
